import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { companyInfo } from '../utils/dataInfo';

// Helper function to get status name
const getStatusName = (status) => {
return status === 1 ? 'Active' : 'Inactive';
};

// Helper function to get branch name
const getBranchName = (branchId, branchesData) => {
if (!branchesData || !Array.isArray(branchesData)) return 'Unknown';
const branch = branchesData.find(br => parseInt(br.id) === parseInt(branchId));
return branch ? branch.name : 'Unknown';
};

// Helper function to split text into multiple lines
const splitTextIntoLines = (doc, text, maxWidth) => {
const words = text.split(' ');
const lines = [];
let currentLine = words[0];

for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = doc.getTextWidth(currentLine + ' ' + word);
    if (width < maxWidth) {
    currentLine += ' ' + word;
    } else {
    lines.push(currentLine);
    currentLine = word;
    }
}
lines.push(currentLine);
return lines;
};

// Manual table drawing function for PDF
const drawDepartmentsTable = (doc, data, startY) => {
const pageWidth = doc.internal.pageSize.width;
const margin = 14;
const rowHeight = 12;

// Column widths for departments
const colWidths = [10, 60, 40, 25, 40, 40]; // #, Department Name, Branch, Status, Created, Updated

// Check if we need a new page
let currentY = startY;
const neededHeight = (data.length + 1) * rowHeight;
if (currentY + neededHeight > doc.internal.pageSize.height - 20) {
    doc.addPage();
    currentY = margin;
}

// Draw table header
doc.setFillColor(255, 255, 255);
doc.setTextColor(25, 118, 210);
doc.setFont(undefined, 'bold');

const headers = ['#', 'Department Name', 'Branch', 'Status', 'Created At',];
let xPos = margin;

// Draw header background
doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

// Draw header text
headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 7);
    xPos += colWidths[index];
});

// Draw header underline
doc.setDrawColor(25, 118, 210);
doc.setLineWidth(0.5);
doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);

currentY += rowHeight;

// Draw table rows
doc.setFont(undefined, 'normal');
doc.setTextColor(0, 0, 0);

data.forEach((row, rowIndex) => {
    // Calculate needed height for this row
    let maxLines = 1;
    const cellLines = row.map((cell, cellIndex) => {
    const text = String(cell);
    const maxWidth = colWidths[cellIndex] - 4;
    const lines = splitTextIntoLines(doc, text, maxWidth);
    maxLines = Math.max(maxLines, lines.length);
    return lines;
    });

    const actualRowHeight = rowHeight * maxLines;

    // Check for page break
    if (currentY + actualRowHeight > doc.internal.pageSize.height - 20) {
    doc.addPage();
    currentY = margin;
    
    // Redraw header on new page
    doc.setFillColor(255, 255, 255);
    doc.setTextColor(25, 118, 210);
    doc.setFont(undefined, 'bold');
    
    // Draw header background
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
    
    xPos = margin;
    headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
    });
    
    // Draw header underline
    doc.setDrawColor(25, 118, 210);
    doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
    
    currentY += rowHeight;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
    doc.setFillColor(248, 248, 248);
    } else {
    doc.setFillColor(255, 255, 255);
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw row text with multi-line support
    cellLines.forEach((lines, cellIndex) => {
    xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
    
    lines.forEach((line, lineIndex) => {
        const lineY = currentY + 7 + (lineIndex * rowHeight);
        doc.text(line, xPos + 2, lineY);
    });
    });
    
    currentY += actualRowHeight;
});

return currentY;
};

// Excel export for Departments
export const exportDepartmentsToExcel = (departments, branchesData = []) => {
try {
    // Prepare data for Excel with all attributes
    const excelData = departments.map((department, index) => ({
    'No.': index + 1,
    'ID': department.id,
    'Department Name': department.name,
    'Additional Info': department.additional_info || '',
    'Branch': getBranchName(department.branch_id, branchesData),
    'Status': getStatusName(department.status),
    'Created Date': new Date(department.created_at).toLocaleDateString(),
    'Updated Date': new Date(department.updated_at).toLocaleDateString()
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const maxWidth = excelData.reduce((widths, row) => {
    Object.keys(row).forEach((key, idx) => {
        const length = String(row[key]).length;
        if (!widths[idx] || length > widths[idx]) {
        widths[idx] = length;
        }
    });
    return widths;
    }, []);
    
    worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Departments');

    // Generate Excel file and download
    const fileName = `departments_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Departments Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
}
};

// Enhanced PDF export for Departments
export const exportDepartmentsToPDFEnhanced = (departments, branchesData = [], filters = {}) => {
try {
    const doc = new jsPDF();
    
    // Set document properties
    doc.setProperties({
    title: 'Departments Detailed Report',
    subject: 'Departments Detailed Export',
    author: companyInfo.name,
    keywords: 'departments, detailed, export, report',
    creator: companyInfo.name
    });
    
    // Title section
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210);
    doc.text('DEPARTMENTS DETAILED REPORT', 105, 25, { align: 'center' });
    
    // Generation details
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    let currentY = 35;
    doc.text(`Report Generated: ${new Date().toLocaleString()}`, 14, currentY);
    currentY += 6;
    doc.text(`Total Departments: ${departments.length}`, 14, currentY);
    currentY += 6;
    
    // Filters applied
    if (filters.name || filters.dateFrom || filters.dateTo || filters.branch) {
    doc.text('Applied Filters:', 14, currentY);
    currentY += 6;
    
    if (filters.name) {
        doc.text(`• Name contains: ${filters.name}`, 20, currentY);
        currentY += 6;
    }
    if (filters.branch) {
        const branchName = getBranchName(filters.branch, branchesData);
        doc.text(`• Branch: ${branchName}`, 20, currentY);
        currentY += 6;
    }
    if (filters.dateFrom) {
        doc.text(`• From date: ${new Date(filters.dateFrom).toLocaleDateString()}`, 20, currentY);
        currentY += 6;
    }
    if (filters.dateTo) {
        doc.text(`• To date: ${new Date(filters.dateTo).toLocaleDateString()}`, 20, currentY);
        currentY += 6;
    }
    currentY += 6;
    }
    
    // Prepare detailed table data with all attributes
    const tableData = departments.map((department, index) => [
    (index + 1).toString(),
    department.name,
    getBranchName(department.branch_id, branchesData),
    getStatusName(department.status),
    new Date(department.created_at).toLocaleDateString()
    ]);
    
    // Draw the table
    drawDepartmentsTable(doc, tableData, currentY + 5);
    
    const fileName = `departments_detailed_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Departments PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
}
};

// CSV export for Departments with all attributes
export const exportDepartmentsToCSV = (departments, branchesData = []) => {
try {
    // CSV headers with all attributes
    const headers = [
    'ID',
    'Department Name',
    'Additional Info',
    'Branch',
    'Status',
    'Created Date',
    'Updated Date'
    ];
    
    // Prepare CSV data with all attributes
    const csvData = departments.map(department => [
    department.id,
    `"${department.name.replace(/"/g, '""')}"`,
    `"${(department.additional_info || '').replace(/"/g, '""')}"`,
    `"${getBranchName(department.branch_id, branchesData).replace(/"/g, '""')}"`,
    getStatusName(department.status),
    `"${new Date(department.created_at).toLocaleDateString()}"`,
    `"${new Date(department.updated_at).toLocaleDateString()}"`
    ]);

    // Create CSV content
    const csvContent = [
    headers.join(','),
    ...csvData.map(row => row.join(','))
    ].join('\n');

    // Create and download the file
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `departments_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
} catch (error) {
    console.error('Departments CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
}
};